import React, { useState } from 'react';
import './dashboard.css';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  Banknote,
  ClipboardList,
  Clock3,
  Eye,
  Hash,
  Plus,
  QrCode,
  ShoppingBag,
  Ticket,
  Trophy,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import PageHeader from '../../components/shared/PageHeader';
import StatCard from '../../components/shared/StatCard';
import {
  EmptyReport,
  money,
  number,
  ReportBars,
  ReportPanel,
  ReportState,
} from '../../components/dashboard/ReportPanel';
import RevenueChart from '../../components/dashboard/RevenueChart';
import CuentaSalesReport from '../../components/dashboard/CuentaSalesReport';
import { TrafficReport, ClarityReport } from '../../components/dashboard/ExternalReports';
import {
  RaffleActivityReport,
  RaffleProgressReport,
} from '../../components/dashboard/RaffleReports';
import useDashboardReports from '../../hooks/useDashboardReports';
import { paths } from '../../routes/paths';

const ACTIONS = [
  { label: 'Crear bono', path: paths.admin.addRifa, icon: Plus },
  { label: 'Revisar reservas', path: paths.admin.reservas, icon: ClipboardList },
  { label: 'Ver tickets', path: paths.admin.tickets, icon: Hash },
  { label: 'Sorteo en vivo', path: paths.admin.liveDraw, icon: Trophy },
  { label: 'Validar ticket', path: paths.admin.redeem, icon: QrCode },
];
const STEPS = [
  { key: 'raffle_view', label: 'Vieron un bono', unit: 'sesiones', icon: Eye },
  { key: 'ticket_selection', label: 'Seleccionaron tickets', unit: 'sesiones', icon: Ticket },
  { key: 'checkout_start', label: 'Iniciaron reserva', unit: 'sesiones', icon: ShoppingBag },
  { key: 'reservation_created', label: 'Crearon reserva', unit: 'reservas', icon: ClipboardList },
  { key: 'payment_method_selected', label: 'Eligieron pago', unit: 'sesiones', icon: Banknote },
];
const STAGES = {
  browse: 'Exploración',
  selection: 'Selección',
  details: 'Datos de reserva',
  payment: 'Pago',
};
const utcDate = value =>
  new Date(value).toLocaleString('es-UY', {
    timeZone: 'UTC',
    dateStyle: 'short',
    timeStyle: 'short',
  });

export default function AdminDashboard() {
  const [range, setRange] = useState('30d');
  const { config, insights, ga4, clarity, rifas } = useDashboardReports(range);
  const data = insights.data;
  const loading = insights.state === 'loading';
  const currency = data?.currency;
  const topRaffles = data?.topRaffles?.slice(0, 5) || [];
  const abandonment = Object.entries(STAGES).map(([key, name]) => ({
    name,
    sessions: data?.abandonment?.[key] ?? 0,
  }));

  return (
    <div className='lottery-dashboard mx-auto max-w-[1600px] space-y-6 pb-4 font-sans'>
      <PageHeader
        title='Dashboard'
        subtitle='Tus bonos, tus resultados y tu próximo gran sorteo.'
      />

      <section
        aria-label='Acciones rápidas'
        className='grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5'
      >
        {ACTIONS.map(({ label, path, icon: Icon }, index) => (
          <Button
            asChild
            key={path}
            variant={index === 0 ? 'default' : 'outline'}
            className={`relative h-auto min-h-[68px] justify-start gap-3 px-4 py-4 shadow-sm transition-shadow hover:shadow-md ${index === 0 ? 'col-span-2 md:col-span-1' : 'bg-card'}`}
          >
            <Link to={path} className='no-underline'>
              <Icon className='h-5 w-5 shrink-0' aria-hidden='true' />
              <span className='min-w-0 whitespace-normal text-left'>{label}</span>
              {index === 1 && data?.pendingReservations != null && (
                <Badge variant='muted' className='absolute right-2 top-1.5 px-1.5 py-0 text-[11px]'>
                  {number(data.pendingReservations)}
                </Badge>
              )}
            </Link>
          </Button>
        ))}
      </section>

      <div className='flex flex-col justify-between gap-4 lg:flex-row lg:items-center'>
        <nav aria-label='Secciones del dashboard' className='flex flex-wrap gap-x-5 gap-y-2'>
          {[
            ['resumen', 'Resumen'],
            ['rifas', 'Bonos'],
            ['recorrido', 'Recorrido'],
            ['trafico', 'Tráfico'],
          ].map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              className='rounded text-sm font-medium text-muted-foreground no-underline transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring'
            >
              {label}
            </a>
          ))}
        </nav>
        <div
          role='group'
          aria-label='Período de analytics'
          className='flex w-fit shrink-0 gap-1 rounded-lg border border-border bg-card p-1'
        >
          {['7d', '30d', '90d'].map(value => (
            <Button
              type='button'
              key={value}
              size='sm'
              variant={range === value ? 'default' : 'ghost'}
              aria-pressed={range === value}
              onClick={() => setRange(value)}
            >
              {value.slice(0, -1)} días
            </Button>
          ))}
        </div>
      </div>

      <section
        id='resumen'
        aria-label='Resumen comercial'
        className='grid scroll-mt-6 gap-4 sm:grid-cols-2 xl:grid-cols-4'
      >
        <StatCard
          className='border-primary/20 bg-primary/5'
          icon={<Banknote />}
          label='Ingresos netos'
          value={money(data?.netRevenue, currency)}
          helpText={currency ? `${currency} · ventas menos reembolsos` : 'Ventas menos reembolsos'}
          loading={loading}
        />
        <StatCard
          icon={<ShoppingBag />}
          label='Ventas confirmadas'
          value={number(data?.confirmedSales)}
          helpText='Confirmaciones del período'
          loading={loading}
        />
        <StatCard
          icon={<ClipboardList />}
          label='Reservas creadas'
          value={number(data?.funnel?.reservation_created)}
          helpText='Nuevas reservas en el período'
          loading={loading}
        />
        <StatCard
          icon={<Clock3 />}
          label='Reservas pendientes'
          value={number(data?.pendingReservations)}
          helpText='Pendientes ahora · todos los períodos'
          loading={loading}
        />
      </section>

      <section className='grid items-start gap-6 xl:grid-cols-[minmax(0,1.8fr)_minmax(280px,1fr)]'>
        <ReportPanel
          title='Un período de movimiento'
          description='Ingresos y ventas confirmadas por día. Primer y último día pueden ser parciales.'
        >
          <ReportState report={insights} label='datos internos'>
            <RevenueChart daily={data?.daily} currency={currency} />
            {data?.from && data?.to && (
              <p className='mb-0 mt-4 text-xs text-muted-foreground'>
                {utcDate(data.from)} — {utcDate(data.to)} · UTC
              </p>
            )}
          </ReportState>
        </ReportPanel>
        <ReportPanel title='Cada venta cuenta' description='El balance del período seleccionado.'>
          <dl className='mb-0 divide-y divide-border'>
            {[
              ['Ingresos brutos', money(data?.grossRevenue, currency)],
              ['Reembolsos', money(data?.refundAmount, currency)],
              ['Ingresos netos', money(data?.netRevenue, currency)],
            ].map(([label, value], index) => (
              <div
                key={label}
                className={`flex flex-wrap items-baseline justify-between gap-2 py-4 first:pt-0 ${index === 2 ? 'font-semibold' : ''}`}
              >
                <dt className='text-sm font-normal text-muted-foreground'>{label}</dt>
                <dd className='mb-0 text-lg tabular-nums'>{value}</dd>
              </div>
            ))}
          </dl>
          <p className='mb-5 text-xs text-muted-foreground'>
            {number(data?.refunds)} reservas reembolsadas en el período.
          </p>
          <Link
            to={paths.admin.reservas}
            className='flex items-center justify-between gap-3 rounded-lg bg-primary/5 p-4 text-sm text-foreground no-underline transition-colors hover:bg-primary/10'
          >
            <span>
              <span className='block font-medium'>
                {number(data?.pendingReservations)} reservas pendientes
              </span>
              <span className='mt-1 block text-xs text-muted-foreground'>
                Revisá las reservas actuales
              </span>
            </span>
            <ArrowUpRight className='h-4 w-4 shrink-0 text-primary' aria-hidden='true' />
          </Link>
        </ReportPanel>
      </section>

      <section id='rifas' className='grid scroll-mt-6 items-start gap-6 xl:grid-cols-2'>
        <ReportPanel
          title='Los bonos que más recaudan'
          description={`Top 5 por ingresos netos del período${currency ? ` · ${currency}` : ''}.`}
        >
          <ReportState report={insights} label='ranking de bonos'>
            {topRaffles.length ? (
              <ReportBars
                rows={topRaffles}
                valueKey='revenue'
                format={value => money(value, currency)}
                color='secondary'
              />
            ) : (
              <EmptyReport>No hay ventas ni reembolsos de bonos en este período.</EmptyReport>
            )}
          </ReportState>
        </ReportPanel>
        <RaffleProgressReport report={rifas} />
      </section>

      <CuentaSalesReport currency={currency} />

      <ReportPanel
        id='recorrido'
        title='De la primera visita a la reserva'
        description='Las etapas de navegación cuentan sesiones; “Crearon reserva” cuenta registros de la base de datos.'
      >
        <ReportState report={insights} label='recorrido de reserva'>
          <ol className='m-0 grid list-none gap-3 p-0 sm:grid-cols-2 xl:grid-cols-5'>
            {STEPS.map(({ key, label, unit, icon: Icon }, index) => (
              <li
                key={key}
                className={`rounded-lg p-4 ${index === 3 ? 'bg-primary/10' : 'bg-muted/60'}`}
              >
                <div className='mb-4 flex items-center justify-between text-primary'>
                  <span className='text-xs font-semibold'>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <Icon className='h-4 w-4' aria-hidden='true' />
                </div>
                <p className='mb-1 text-3xl font-semibold tracking-tight tabular-nums'>
                  {number(data?.funnel?.[key])}
                </p>
                <p className='mb-1 text-sm font-medium'>{label}</p>
                <p className='mb-0 text-xs text-muted-foreground'>{unit}</p>
              </li>
            ))}
          </ol>
        </ReportState>
      </ReportPanel>

      <ReportPanel
        title='¿Dónde se detienen?'
        description='Abandono por etapa · sesiones sin reserva tras 30 minutos de inactividad.'
      >
        <ReportState report={insights} label='abandono por etapa'>
          {abandonment.some(row => row.sessions) ? (
            <ReportBars rows={abandonment} valueKey='sessions' color='warning' unit='sesiones' />
          ) : (
            <EmptyReport>No se registró abandono en este período.</EmptyReport>
          )}
        </ReportState>
      </ReportPanel>

      <section id='trafico' className='scroll-mt-6 space-y-6' aria-label='Tráfico del sitio'>
        {config.state !== 'ready' && (
          <ReportPanel title='Tráfico del sitio'>
            <ReportState report={config} label='configuración de analytics' />
          </ReportPanel>
        )}
        {config.state === 'ready' &&
          !config.data?.ga4MeasurementId &&
          !config.data?.clarityProjectId && (
            <ReportPanel title='Tráfico del sitio'>
              <EmptyReport>
                Las integraciones de tráfico no están activadas para este sitio. Las métricas
                comerciales siguen disponibles.
              </EmptyReport>
            </ReportPanel>
          )}
        {config.data?.ga4MeasurementId && <TrafficReport report={ga4} />}
      </section>
      {data && <RaffleActivityReport rows={data.raffles} currency={currency} />}
      {config.data?.clarityProjectId && <ClarityReport report={clarity} />}
    </div>
  );
}
